import type { ImagesService } from '@/engine/app/modules/images/Images.service';
import type { SlicePositionInfo, SliceOrientationInfo } from '@/engine/app/modules/images/ImageStackController';
import { SliceType } from '@/engine/app/modules/images/interfaces';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import type { IClippingController } from './interfaces';
import * as THREE from 'three';
import type { CallbackSubscription } from '@/engine/Utilities/CallbackManager';
import { CallbackManager } from '@/engine/Utilities/CallbackManager';
import type { ViewsService } from '@/engine/app/Views.service';
import type { ViewPortType } from '@/engine/app/interfaces';

export class ImagesClippingController implements IClippingController {
  private readonly _imagesService: ImagesService;

  private readonly _viewsService: ViewsService;

  private readonly _sliceType: SliceType;

  private readonly _planeType: ViewPortType;

  private _slicePlane: THREE.Plane;

  private _sliceOrientationInfo?: SliceOrientationInfo;

  private _subscriptions: DisposablesBasket = new DisposablesBasket();

  private readonly _planeChangedListeners: CallbackManager<(plane: THREE.Plane) => void>;

  private readonly _enabledChangedListeners: CallbackManager<(plane: THREE.Plane | null) => void>;

  public constructor(
    sliceType: SliceType,
    imagesService: ImagesService,
    viewsService: ViewsService,
    descriptor: string
  ) {
    this._imagesService = imagesService;
    this._sliceType = sliceType;
    this._viewsService = viewsService;
    this._planeType = descriptor as ViewPortType;
    this._planeChangedListeners = new CallbackManager();
    this._enabledChangedListeners = new CallbackManager();
    this._slicePlane = new THREE.Plane();

    this._subscriptions.add(
      this._imagesService.subscribeToSliceOrientationChange(
        this._sliceType,
        this._handleSliceOrientationChanged.bind(this)
      )
    );
    this._subscriptions.add(
      this._imagesService.subscribeToSlicePositionChange(this._sliceType, this._handleSlicePositionChange.bind(this))
    );
  }

  public dispose(): void {
    this._subscriptions.dispose();
  }

  public setEnabled(enabled: boolean): void {
    this._enabledChangedListeners.invoke(enabled ? this._slicePlane : null);
  }

  public isEnabled(): boolean {
    const plane = this._viewsService.currentViewOrThrow.clippingPlanes.get(this._planeType);
    return !!plane;
  }

  public getPlane(): THREE.Plane | undefined {
    return this._viewsService.currentViewOrThrow.clippingPlanes.get(this._planeType);
  }

  public setPlane(plane: THREE.Plane): void {
    const currentPlane = this.getPlane();
    const isFlipped = currentPlane ? this.isFlipped(currentPlane) : false;
    this._slicePlane = isFlipped ? plane.negate() : plane;
    this._planeChangedListeners.invoke(this._slicePlane);
  }

  public trackEnabledChanged(callback: (plane: THREE.Plane | null) => void): CallbackSubscription {
    return this._enabledChangedListeners.subscribe(callback);
  }

  public trackPlaneChanged(callback: (plane: THREE.Plane) => void): CallbackSubscription {
    return this._planeChangedListeners.subscribe(callback);
  }

  private _handleSliceOrientationChanged(orientationInfo: SliceOrientationInfo): void {
    this._sliceOrientationInfo = orientationInfo;
  }

  private _handleSlicePositionChange(positionInfo: SlicePositionInfo): void {
    if (!this._sliceOrientationInfo) throw new Error('Slice Orientation Info must be obtained by this point');
    // TODO: [chebotarev] explicit CORONAl image's specific behavior is bad
    const normal = new THREE.Vector3(0, 0, this._sliceType == SliceType.CORONAL ? -1 : 1);
    const slicePlane = new THREE.Plane(normal, 0).applyMatrix4(positionInfo.sliceCS);
    this.setPlane(slicePlane);
  }

  public flipPlane(): void {
    const currentPlane = this.getPlane();
    if (!currentPlane) return;
    this._planeChangedListeners.invoke(currentPlane.negate());
  }

  public isFlipped(plane: THREE.Plane): boolean {
    const sliceCS = this._imagesService.getSlicePositionInfo(this._sliceType)?.sliceCS;

    if (!sliceCS) {
      throw new Error('sliceCS matrix is undefined');
    }

    const originalNormal = new THREE.Vector3()
      .set(sliceCS.elements[8], sliceCS.elements[9], sliceCS.elements[10])
      .normalize();
    // TODO: [chebotarev] explicit CORONAl image's specific behavior is bad
    return this._sliceType === SliceType.CORONAL
      ? plane.normal.dot(originalNormal) > 0
      : plane.normal.dot(originalNormal) < 0;
  }
}
